/**
 * Планувальник інтервального повторення: FSRS-4.5 і SM-2.
 *
 * Логіка навмисно винесена в чисті функції без побічних ефектів і без знання про
 * D1: ті самі функції крутяться в тестах (node --test) і в браузері. Сервер лише
 * зберігає результат, тому алгоритм можна змінити без міграції даних.
 *
 * Оцінки пам'яті (кнопки на картці):
 *   hard  «Сложно»   → згадав, але важко / з помилкою
 *   good  «Нормально» → згадав правильно
 *   easy  «Идеально»  → згадав миттєво
 *   again «Забув»     → не згадав (внутрішній стан, доступний через lapseAgain)
 */

export const GRADES = ['again', 'hard', 'good', 'easy']

/** Три кнопки в UI → чотири оцінки FSRS. */
export const GRADE_BY_BUTTON = {
  hard: 'hard',
  normal: 'good',
  perfect: 'easy',
}

export const STATE = { NEW: 0, LEARNING: 1, REVIEW: 2, RELEARNING: 3 }

export const DAY_MS = 86_400_000

/**
 * Оцінка → число за нотацією FSRS (G = 1..4).
 * Ваги FSRS індексуються саме числами, тому переклад робиться один раз,
 * на вході в алгоритм, і далі по коду ходять лише числа.
 */
const GRADE_VALUE = { again: 1, hard: 2, good: 3, easy: 4 }

/** Стан нової картки (те, що приходить із D1 для state = 0). */
export const newState = (now = Date.now()) => ({
  state: STATE.NEW,
  interval: 0,
  easeFactor: 2.5,
  stability: 0,
  difficulty: 0,
  reps: 0,
  lapses: 0,
  dueDate: new Date(now).toISOString(),
  lastReviewedAt: null,
  lastGrade: null,
})

/** Нормалізує те, що прийшло з D1, до повного стану. */
export function normalizeState(raw = {}, now = Date.now()) {
  return {
    state: Number(raw.state ?? 0),
    interval: Number(raw.interval ?? 0),
    easeFactor: Number(raw.ease_factor ?? raw.easeFactor ?? 2.5),
    stability: Number(raw.stability ?? 0),
    difficulty: Number(raw.difficulty ?? 0),
    reps: Number(raw.reps ?? 0),
    lapses: Number(raw.lapses ?? 0),
    dueDate: raw.due_date ?? raw.dueDate ?? new Date(now).toISOString(),
    lastReviewedAt: raw.last_reviewed_at ?? raw.lastReviewedAt ?? null,
    lastGrade: raw.last_grade ?? raw.lastGrade ?? null,
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  FSRS-4.5
// ═══════════════════════════════════════════════════════════════════════════

/** Ваги FSRS-4.5 (опубліковані дефолтні). Можна підставити власні після opt. */
export const FSRS_W = [
  0.2172, 1.1771, 3.2602, 16.1507, 7.0114, 0.5700, 2.0966, 0.0069, 1.5261, 0.1120,
  1.0178, 1.8490, 0.1133, 0.3127, 2.2934, 0.2191, 3.0004, 0.7536, 0.3332,
]

const DECAY = -0.5
const FACTOR = 0.9 ** (1 / DECAY) - 1 // 19/81
const MAX_INTERVAL_DAYS = 365 * 5

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi)

/** Поточна ймовірність пригадування за часом (power forgetting curve FSRS-4.5). */
export function retrievability(elapsedDays, stability) {
  if (stability <= 0) return 0
  return (1 + (FACTOR * elapsedDays) / stability) ** DECAY
}

/** Скільки днів триватиме інтервал при бажаній ймовірності. */
export function intervalForRetention(stability, retention = 0.9) {
  return (stability / FACTOR) * (retention ** (1 / DECAY) - 1)
}

/** Стабільність нової картки: залежить лише від оцінки (G = 1..4). */
const initStability = (g, w) => w[g - 1]

/** Складність нової картки: D0(G) = w4 − e^(w5·G) + 1 */
const initDifficulty = (g, w) => clamp(w[4] - Math.exp(w[5] * g) + 1, 1, 10)

/**
 * Повний крок FSRS.
 * @param {object} s   normalizeState(...) — поточний стан
 * @param {'again'|'hard'|'good'|'easy'} grade
 * @param {{ now?: number, desiredRetention?: number, w?: number[] }} [opts]
 */
export function fsrsNext(s, grade, opts = {}) {
  const w = opts.w ?? FSRS_W
  const now = opts.now ?? Date.now()
  const retention = opts.desiredRetention ?? 0.9
  // У FSRS ваги індексуються числом оцінки, а не рядком — перекладаємо один раз.
  const g = GRADE_VALUE[grade] ?? GRADE_VALUE.good
  const elapsedDays = s.lastReviewedAt
    ? Math.max(0, (now - new Date(s.lastReviewedAt).getTime()) / DAY_MS)
    : s.interval || 0

  const isReview = s.state >= STATE.REVIEW && s.stability > 0
  const D0 = initDifficulty(g, w)
  const D = isReview ? s.difficulty || D0 : 0
  const S = isReview ? s.stability : 0
  const R = isReview ? retrievability(elapsedDays, S) : 0

  let stability
  let difficulty

  if (grade === 'again') {
    // Стабільність після забування
    const sf =
      w[11] *
      Math.pow(D || D0, -w[12]) *
      (Math.pow(S + 1, w[13]) - 1) *
      Math.exp((1 - R) * w[14])
    // Забування не може зробити картку «міцнішою»: стабільність обмежена зверху
    // мінімальною стабільністю для цієї складності (w17 · D^(−w18)).
    const ceiling = w[17] * Math.pow(D || D0, -w[18])
    stability = Math.min(clamp(sf, 0.1, 36_500), ceiling)
    difficulty = nextDifficulty(D || D0, g, w)
  } else {
    // Стабільність після успішного пригадування
    const hardPenalty = g === 2 ? w[15] : 1
    const easyBonus = g === 4 ? w[16] : 1
    const recall =
      1 +
      Math.exp(w[8]) *
        (11 - (D || D0)) *
        Math.pow(S || initStability(g, w), -w[9]) *
        (Math.exp((1 - R) * w[10]) - 1) *
        hardPenalty *
        easyBonus
    stability = clamp(S > 0 ? S * recall : initStability(g, w), 0.1, 36_500)
    difficulty = nextDifficulty(D || D0, g, w)
  }

  const interval = clamp(intervalForRetention(stability, retention), grade === 'again' ? 0.001 : 1, MAX_INTERVAL_DAYS)
  const isLapse = grade === 'again' && s.state >= STATE.REVIEW

  return {
    state: isLapse ? STATE.RELEARNING : STATE.REVIEW,
    interval: isLapse ? Math.max(1, Math.round(Math.min(interval, 1) * 10) / 10) : Math.round(interval * 10) / 10,
    easeFactor: s.easeFactor,
    stability: round2(stability),
    difficulty: round2(difficulty),
    reps: s.reps + 1,
    lapses: s.lapses + (isLapse ? 1 : 0),
    dueDate: new Date(now + interval * DAY_MS).toISOString(),
    lastReviewedAt: new Date(now).toISOString(),
    lastGrade: grade,
  }
}

/** ΔD = −w6·(G−3), потім лінійне стягнення до 10 і реверсія до середнього. */
function nextDifficulty(D, g, w) {
  const delta = -w[6] * (g - 3)
  let d = D + delta * ((10 - D) / 9)
  d = w[7] * initDifficulty(4, w) + (1 - w[7]) * d
  return clamp(d, 1, 10)
}

// ═══════════════════════════════════════════════════════════════════════════
//  SM-2 (сумісність із класичним алгоритмом, ease_factor у межах 1.3..3.0)
// ═══════════════════════════════════════════════════════════════════════════

export function sm2Next(s, grade, opts = {}) {
  const now = opts.now ?? Date.now()
  const isLapse = grade === 'again' && s.state >= STATE.REVIEW

  let ease = s.easeFactor || 2.5
  if (grade === 'hard') ease -= 0.15
  else if (grade === 'easy') ease += 0.15
  ease = clamp(ease, 1.3, 3.0)

  let interval
  const reps = s.reps + 1

  if (isLapse) {
    interval = 1
  } else if (reps === 1) {
    interval = grade === 'easy' ? 4 : 1
  } else if (reps === 2) {
    interval = grade === 'hard' ? 2 : grade === 'easy' ? 7 : 6
  } else {
    const base = s.interval || 1
    interval = grade === 'hard' ? base * 1.2 : grade === 'easy' ? base * ease * 1.3 : base * ease
  }

  interval = clamp(Math.round(interval * 10) / 10, 1, MAX_INTERVAL_DAYS)

  return {
    state: isLapse ? STATE.RELEARNING : STATE.REVIEW,
    interval,
    easeFactor: round2(ease),
    // SM-2 не має стабільності/складності, але тримаємо їх у sync для статистики
    stability: round2(Math.max(interval, s.stability || 0)),
    difficulty: round2(
      clamp(
        (s.difficulty || 5) + (grade === 'hard' ? 0.5 : grade === 'easy' ? -0.5 : 0),
        1,
        10,
      ),
    ),
    reps,
    lapses: s.lapses + (isLapse ? 1 : 0),
    dueDate: new Date(now + interval * DAY_MS).toISOString(),
    lastReviewedAt: new Date(now).toISOString(),
    lastGrade: grade,
  }
}

// ═══════════════════════════════════════════════════════════════════════════

export const ALGORITHMS = {
  fsrs: { label: 'FSRS-4.5', run: fsrsNext, default: true },
  sm2: { label: 'SM-2', run: sm2Next, default: false },
}

/**
 * Єдина точка входу: рахує наступний стан і одразу показує, коли картка повернеться.
 * @returns {{ next: object, preview: Record<'hard'|'good'|'easy'|'again', string> }}
 */
export function schedule(current, button, opts = {}) {
  const algo = ALGORITHMS[opts.algorithm] ?? ALGORITHMS.fsrs
  const algorithm = ALGORITHMS[opts.algorithm] ? opts.algorithm : 'fsrs'
  const s = normalizeState(current, opts.now)
  // Невідома кнопка не має потрапити в базу — рахуємо як «Нормально».
  const grade = GRADE_BY_BUTTON[button] ?? GRADE_BY_BUTTON.normal

  const next = algo.run(s, grade, opts)
  const preview = {}
  for (const g of GRADES) {
    preview[g] = formatInterval(algo.run(s, g, opts).interval)
  }

  return { next, preview, grade, algorithm }
}

/** «через 10 хв» / «через 3 дні» — для підписів на кнопках оцінки. */
export function formatInterval(days) {
  const d = Number(days) || 0
  if (d <= 0) return '< 1 хв'
  // Менше години — у хвилинах, інакше підпис «2 дні» насправді означав би 1 тиждень.
  const minutes = d * 1440
  if (minutes < 60) return `${Math.max(1, Math.round(minutes))} хв`
  if (d < 1) return `${Math.round(d * 24)} год`
  if (d < 30) return `${Math.round(d)} дн`
  if (d < 365) return `${Math.round(d / 30.44)} міс`
  return `${(d / 365).toFixed(1).replace('.', ',')} р`
}

/** Через скільки реально прийде картка — з урахуванням поточної дати. */
export function timeUntil(dueDate, now = Date.now()) {
  if (!dueDate) return 'зараз'
  const ms = new Date(dueDate).getTime() - now
  if (ms <= 0) return 'зараз'
  return `через ${formatInterval(ms / DAY_MS)}`
}

const round2 = (v) => Math.round(v * 100) / 100
